import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderOpen, Plus, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchDossiers } from "@/lib/dossiers";
import DossierRow from "@/components/dossiers/DossierRow";
import DossierFilters, { EMPTY_FILTERS, applyFilters } from "@/components/dossiers/DossierFilters";
import DossierSortHeader, { DEFAULT_SORT, applySort } from "@/components/dossiers/DossierSortHeader";

export default function Dashboard() {
    const [dossiers, setDossiers] = useState([]);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [sort, setSort] = useState(DEFAULT_SORT);
    const filtered = applySort(applyFilters(dossiers, filters), sort);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        fetchDossiers()
            .then(setDossiers)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    const count = (s) => dossiers.filter((d) => d.statut_dossier === s).length;
    const eleves = dossiers.filter((d) => d.niveau_risque === "Élevé").length;
    const stats = [
        { label: "Dossiers ouverts", value: count("en_cours") },
        { label: "En attente de documents", value: count("en_attente_documents") },
        { label: "Risque élevé", value: eleves, alert: eleves > 0 },
    ];
    const setRisque = (id, n, s) => setDossiers((ds) => ds.map((d) => (d.id === id ? { ...d, niveau_risque: n, niveau_risque_source: s } : d)));
    const renamed = (id, nom_mission) => setDossiers((ds) => ds.map((d) => (d.id === id ? { ...d, nom_mission } : d)));
    const deleted = (id) => setDossiers((ds) => ds.filter((d) => d.id !== id));

    return (
        <div data-testid="dashboard-page" className="space-y-10">
            <header className="flex items-end justify-between gap-6 border-b border-border pb-8">
                <div>
                    <p data-testid="page-eyebrow" className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                        Data Room
                    </p>
                    <h1 data-testid="page-title" className="mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
                        Mes dossiers <span className="italic text-muted-foreground">en cours</span>
                    </h1>
                    <p className="mt-4 max-w-xl text-sm text-muted-foreground">
                        Suivez l&apos;ensemble de vos missions d&apos;audit financier et de restructuration depuis un
                        point d&apos;entrée unique.
                    </p>
                </div>
                <Button
                    asChild
                    data-testid="btn-new-dossier"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-5 h-11 shadow-sm"
                >
                    <Link to="/nouveau-dossier">
                        <Plus className="h-4 w-4" strokeWidth={2} />
                        Nouveau dossier
                    </Link>
                </Button>
            </header>

            <section data-testid="stat-row" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {stats.map((s) => (
                    <Card key={s.label} className="border-slate-200 bg-white shadow-sm">
                        <CardContent className="pt-6">
                            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{s.label}</p>
                            <p className={`mt-3 font-display text-4xl ${s.alert ? "text-rose-700" : "text-foreground"}`}>
                                {loading ? "–" : s.value}
                            </p>
                        </CardContent>
                    </Card>
                ))}
            </section>

            <section data-testid="dossiers-section">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-2xl text-foreground">Liste des dossiers</h2>
                    <span data-testid="dossiers-count" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        {filtered.length}{filtered.length !== dossiers.length ? ` / ${dossiers.length}` : ""} résultat{filtered.length > 1 ? "s" : ""}
                    </span>
                </div>
                {dossiers.length > 0 && (
                    <div className="mb-5">
                        <DossierFilters filters={filters} onChange={setFilters} />
                    </div>
                )}

                {loading ? (
                    <div data-testid="dossiers-loading" className="space-y-2">
                        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
                    </div>
                ) : error ? (
                    <Card data-testid="dossiers-error" className="border-destructive/30 bg-destructive/5 shadow-sm">
                        <CardContent className="py-6 flex gap-3 text-sm text-destructive">
                            <AlertTriangle className="h-5 w-5 shrink-0" strokeWidth={1.75} />
                            <div>
                                <p className="font-medium">Impossible de charger les dossiers</p>
                                <p className="mt-1 text-destructive/80">{error}</p>
                                <p className="mt-2 text-muted-foreground">
                                    Vérifiez que le script <code>supabase_schema.sql</code> a bien été exécuté dans votre projet Supabase.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                ) : dossiers.length === 0 ? (
                    <Card data-testid="dossiers-empty-state" className="border-dashed border-border bg-transparent shadow-sm">
                        <CardHeader className="items-center text-center pt-14">
                            <div className="h-14 w-14 rounded-full bg-accent flex items-center justify-center mb-4">
                                <FolderOpen className="h-6 w-6 text-primary" strokeWidth={1.5} />
                            </div>
                            <CardTitle className="font-display text-2xl font-normal">Aucun dossier pour le moment</CardTitle>
                            <CardDescription className="max-w-md pt-1">
                                Créez votre premier dossier pour lancer un audit financier ou une mission de restructuration.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="pb-14 flex justify-center">
                            <Button
                                asChild
                                data-testid="btn-create-first-dossier"
                                variant="outline"
                                className="rounded-full h-10 px-5 border-primary/20 text-primary hover:bg-accent"
                            >
                                <Link to="/nouveau-dossier">
                                    <Plus className="h-4 w-4" strokeWidth={2} />
                                    Créer un dossier
                                </Link>
                            </Button>
                        </CardContent>
                    </Card>
                ) : filtered.length === 0 ? (
                    <Card data-testid="dossiers-no-match" className="border-dashed border-border bg-transparent shadow-sm">
                        <CardContent className="py-10 text-center text-sm text-muted-foreground">
                            Aucun dossier ne correspond à ces critères.
                        </CardContent>
                    </Card>
                ) : (
                    <div data-testid="dossiers-list-wrapper" className="rounded-xl border border-slate-200 bg-white shadow-sm">
                        <DossierSortHeader sort={sort} onChange={setSort} />
                        <ul data-testid="dossiers-list" className="divide-y divide-border">
                            {filtered.map((d) => <DossierRow key={d.id} dossier={d} onRisqueChange={setRisque} onRenamed={renamed} onDeleted={deleted} />)}
                        </ul>
                    </div>
                )}
            </section>
        </div>
    );
}
